import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  clearAccessToken,
  getAccessToken,
  getMustChangePassword,
  storeAccessToken,
  storeMustChangePassword,
} from "@/app/tokenStorage";
import { store } from "@/app/store/store";
import { baseApi } from "@/app/store/baseApi";

interface SessionContextValue {
  isAuthenticated: boolean;
  mustChangePassword: boolean;
  login: (accessToken: string, mustChangePassword: boolean) => void;
  logout: () => void;
  clearMustChangePassword: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

function hasStoredSession(): boolean {
  return getAccessToken() !== null;
}

// Single source of truth for "is this tab logged in?" — route guards,
// TopBar, and AuthPage all read/write auth state through here instead of
// touching tokenStorage directly, so there's exactly one place that decides
// what "authenticated" means and one place other tabs/requests can signal
// into.
export function SessionProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(hasStoredSession);
  const [mustChangePassword, setMustChangePassword] = useState(getMustChangePassword);

  useEffect(() => {
    function syncFromStorage() {
      setIsAuthenticated(hasStoredSession());
    }

    // "storage" fires in *other* tabs when localStorage changes here — keeps
    // every open tab in sync (log out in one tab, every tab signs out).
    // "lendcord:session-expired" fires in *this* tab when apiClient sees a
    // 401 on an authenticated request (expired/revoked token) — see
    // apiClient.ts. Both cases reduce to "re-check whether a token exists".
    window.addEventListener("storage", syncFromStorage);
    window.addEventListener("lendcord:session-expired", syncFromStorage);
    return () => {
      window.removeEventListener("storage", syncFromStorage);
      window.removeEventListener("lendcord:session-expired", syncFromStorage);
    };
  }, []);

  // RTK Query's cache is keyed by endpoint+args, not by user — useGetProfileQuery()
  // takes no args, so a different user's login after this one would otherwise
  // still see the previous user's cached name/profile (a stale UI, not a stale
  // token — the new requests do carry the new Bearer token) until something
  // happens to trigger a refetch. Clearing the cache the moment a session ends
  // (explicit logout, cross-tab logout, or an apiClient 401) guarantees the
  // next login always starts from an empty cache, regardless of which of
  // those three ended the previous one.
  useEffect(() => {
    if (!isAuthenticated) {
      store.dispatch(baseApi.util.resetApiState());
    }
  }, [isAuthenticated]);

  function login(accessToken: string, mustChangePasswordFlag: boolean) {
    storeAccessToken(accessToken);
    storeMustChangePassword(mustChangePasswordFlag);
    setIsAuthenticated(true);
    setMustChangePassword(mustChangePasswordFlag);
  }

  function logout() {
    clearAccessToken();
    storeMustChangePassword(false);
    setIsAuthenticated(false);
    setMustChangePassword(false);
  }

  // Called once /auth/password/change succeeds for a forced (mustChangePassword)
  // rotation — lets the caller past ForcedPasswordChangeGate without a full
  // logout/login round trip. A voluntary change via ChangePasswordDialog never
  // has the flag set in the first place, so this is a no-op there.
  function clearMustChangePassword() {
    storeMustChangePassword(false);
    setMustChangePassword(false);
  }

  return (
    <SessionContext.Provider value={{ isAuthenticated, mustChangePassword, login, logout, clearMustChangePassword }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}
